import { z } from 'zod';
import { OrganizationFocusAreaModes } from 'libs/end-to-end/src/focus-area';
import { OrganizationUserRole } from 'libs/end-to-end/src/types/users/organization-user-roles';

// All these types are used by the admin panel, so the prefix "Admin" is used to denote that.
// It does not mean that they are indeed admins.

export const AdminUserEnrollmentType = z.union([
  z.literal('local'),
  z.literal('sso-automatic'),
]);

export type AdminUserEnrollmentType = z.infer<typeof AdminUserEnrollmentType>;

export const ExtraOrganization = z.object({
  organizationId: z.string().min(1),
  organizationRole: OrganizationUserRole,
});

export type ExtraOrganization = z.infer<typeof ExtraOrganization>;

export const ExtraOrganizations = z.array(ExtraOrganization);

export type ExtraOrganizations = z.infer<typeof ExtraOrganizations>;

export const AdminUser = z.object({
  id: z.string().min(1),
  email: z.string().min(1),
  organizationId: z.string().nullable(),
  organizationName: z.string().nullable(),
  organizationRole: OrganizationUserRole.nullable(),
  isSuperAdmin: z.boolean(),
  enrollmentType: AdminUserEnrollmentType,
  extraOrganizations: ExtraOrganizations,
});

export type AdminUser = z.infer<typeof AdminUser>;

export const UpdateAdminUser = z.object({
  email: z.string().min(1),
  organizationId: z.string().nullable(),
  organizationRole: OrganizationUserRole.nullable(),
  isSuperAdmin: z.boolean(),
  password: z.string().nullable(),
  extraOrganizations: ExtraOrganizations,
});

export type UpdateAdminUser = z.infer<typeof UpdateAdminUser>;

export const CreateAdminUser = z.object({
  email: z.string().min(1),
  organizationId: z.string().nullable(),
  organizationRole: OrganizationUserRole.nullable(),
  isSuperAdmin: z.boolean(),
  password: z.string().min(1),
  extraOrganizations: ExtraOrganizations,
});

export type CreateAdminUser = z.infer<typeof CreateAdminUser>;

export const ShareFileConfig = z.object({
  oauthUrl: z.string(),
  apiBaseUrl: z.string(),
  folderId: z.string(),
  fileName: z.string(),
});

export const WorkdayConfig = z.object({
  employeeHistory: z.string(),
  staffingEvents: z.string(),
  compensationChangeHistory: z.string(),
  candidates: z.string(),
});

export type WorkdayConfig = z.infer<typeof WorkdayConfig>;

export type ShareFileConfig = z.infer<typeof ShareFileConfig>;

export const MutationType = z.union([
  z.literal('none'),
  z.literal('update'),
  z.literal('delete'),
]);

export type MutationType = z.infer<typeof MutationType>;

const NoMutation = z.object({
  mutationType: z.literal('none'),
});

const UpdateMutation = z.object({
  mutationType: z.literal('update'),
  newValue: z.string().min(1),
});

const DeleteMutation = z.object({
  mutationType: z.literal('delete'),
});

export const SecretMutation = z.union([
  NoMutation,
  UpdateMutation,
  DeleteMutation,
]);

export const SecretMutations = z.object({
  Greenhouse: SecretMutation,
  Workday: SecretMutation,
  WorkdayUsername: SecretMutation,
  ShareFileClientId: SecretMutation,
  ShareFileClientSecret: SecretMutation,
  ShareFileUsername: SecretMutation,
  ShareFilePassword: SecretMutation,
});

export type SecretMutation = z.infer<typeof SecretMutation>;
export type SecretMutations = z.infer<typeof SecretMutations>;

export const SecretPresence = z.object({
  Greenhouse: z.boolean(),
  Workday: z.boolean(),
  WorkdayUsername: z.boolean(),
  ShareFileClientId: z.boolean(),
  ShareFileClientSecret: z.boolean(),
  ShareFileUsername: z.boolean(),
  ShareFilePassword: z.boolean(),
});

export type SecretPresence = z.infer<typeof SecretPresence>;

export const IntegrationAuthorizations = z.object({
  greenhouse: z.boolean(),
  workday: z.boolean(),
  shareFile: z.boolean(),
  executiveSummary: z.boolean(),
  hiringCopilot: z.boolean(),
  hiringCopilotUpsell: z.boolean(),
  promotionCopilot: z.boolean(),
  promotionCopilotUpsell: z.boolean(),
  payBenchmarking: z.boolean(),
  payBenchmarkingUpsell: z.boolean(),
  payCorrection: z.boolean(),
  payCorrectionUpsell: z.boolean(),
});

export type IntegrationAuthorizations = z.infer<
  typeof IntegrationAuthorizations
>;

export const UpdateAdminOrganization = z.object({
  name: z.string().min(1),
  integrationAuthorizations: IntegrationAuthorizations,
  focusAreaModes: OrganizationFocusAreaModes,
  workdayConfig: WorkdayConfig,
  secretMutations: SecretMutations,
  shareFileConfig: ShareFileConfig,
});

export type UpdateAdminOrganization = z.infer<typeof UpdateAdminOrganization>;

export const CreateAdminOrganization = z.object({
  name: z.string().min(1),
  integrationAuthorizations: IntegrationAuthorizations,
  focusAreaModes: OrganizationFocusAreaModes,
  workdayConfig: WorkdayConfig,
  shareFileConfig: ShareFileConfig,
  secretMutations: SecretMutations,
});

export type CreateAdminOrganization = z.infer<typeof CreateAdminOrganization>;

export const AdminUsers = z.object({
  users: z.array(AdminUser),
});

export type AdminUsers = z.infer<typeof AdminUsers>;

export const AdminOrganization = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  integrationAuthorizations: IntegrationAuthorizations,
  focusAreaModes: OrganizationFocusAreaModes,
  workdayConfig: WorkdayConfig,
  shareFileConfig: ShareFileConfig,
  secretPresence: SecretPresence,
});

export type AdminOrganization = z.infer<typeof AdminOrganization>;

export const AdminOrganizations = z.object({
  organizations: z.array(AdminOrganization),
});

export type AdminOrganizations = z.infer<typeof AdminOrganizations>;

export const AdminSuppression = z.object({
  emailAddress: z.string().min(1),
  reason: z.string().nullable(),
  lastUpdateTime: z.number(),
});

export type AdminSuppression = z.infer<typeof AdminSuppression>;

export const AdminSuppressions = z.object({
  suppressions: z.array(AdminSuppression),
  nextToken: z.string().nullable(),
});

export type AdminSuppressions = z.infer<typeof AdminSuppressions>;

export const DeleteAdminSuppression = z.object({
  emailAddress: z.string().min(1),
});

export type DeleteAdminSuppression = z.infer<typeof DeleteAdminSuppression>;

export const AdminOrganizationUpload = z.object({
  id: z.string().min(1),
  organizationId: z.string().min(1),
  name: z.string().min(1),
  isHidden: z.boolean(),
});

export type AdminOrganizationUpload = z.infer<typeof AdminOrganizationUpload>;

export const AdminOrganizationUploads = z.object({
  uploads: z.array(AdminOrganizationUpload),
});

export type AdminOrganizationUploads = z.infer<typeof AdminOrganizationUploads>;

export const UpdateAdminOrganizationUpload = z.object({
  name: z.string().min(1),
  isHidden: z.boolean(),
});

export type UpdateAdminOrganizationUpload = z.infer<
  typeof UpdateAdminOrganizationUpload
>;
